import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { SPFCovenantPayload } from '@/libs/fleet-api/services/spf';
import { FileData } from '@/libs/fleet-api/services/types';
import {
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Upload,
	UploadFile,
	InputNumber,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FC, Fragment, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import Radio from 'antd/es/radio/radio';
import { useUserData } from '@/libs/fleet-api/hooks';

type CovenantModalParam =
	| {
			action: 'new';
	  }
	| {
			action: 'edit' | 'edit-all';
			record: SPFCovenantPayload;
	  };

export const useCovenantModal = () =>
	useAsyncModal<CovenantModalParam, SPFCovenantPayload>();

export type CovenantModalProps = ReturnType<typeof useCovenantModal> &
	Partial<
		Record<
			| 'categoryOptions'
			| 'priorityOptions'
			| 'repeatFrequencyOptions'
			| 'monitoringType'
			| 'furtherAnalysisOptions'
			| 'customRepeatFrequencyOptions'
			| 'monthOptions'
			| 'positionOptions'
			| 'dayOfWeekOptions'
			| 'statusOptions',
			MasterData[]
		>
	> & {
		customerId?: string;
		prospectId?: string;
	};

const REPEAT_CUSTOM = { id: 6, value: 'Custom' } as const satisfies MasterData;

enum REPEAT_FREQUENCY {
	Day = 1,
	Week,
	Month,
	Year,
}

const CovenantModal: FC<CovenantModalProps> = ({
	modal,
	close,
	data,
	customerId,
	prospectId,
	priorityOptions = [],
	categoryOptions = [],
	furtherAnalysisOptions = [],
	monitoringType = [],
	dayOfWeekOptions = [],
	monthOptions = [],
	positionOptions = [],
	repeatFrequencyOptions = [],
	customRepeatFrequencyOptions = [],
	statusOptions = [],
}) => {
	const userData = useUserData();
	const { groupName } = userData ?? {};
	const { form, fieldName, value } = useForm<SPFCovenantPayload>();
	const editAllField = data?.action === 'edit-all' || data?.action === 'new';

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const [on, setOn] = useState(true);

	const customRepeatType = useMemo(() => {
		const id = value?.repeatFrequencyCustom?.repeatFrequencyId;
		if (id === 1) return 'day';
		if (id === 2) return 'week';
		if (id === 3) return 'month';
		if (id === 4) return 'year';
		return '';
	}, [value?.repeatFrequencyCustom?.repeatFrequencyId]);

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="CANCEL"
			okText="SAVE"
			onCancel={() => close()}
			onOk={form.submit}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (data && data?.action !== 'new') {
					form.setFieldsValue({
						...data.record,
						repeatFrequencyId: data.record.repeatFrequencyCustom
							? REPEAT_CUSTOM.id
							: data.record.repeatFrequencyId,
					});
					setOn(
						Boolean(data.record.repeatFrequencyCustom?.onPattern?.dayOfMonth),
					);
				} else {
					form.resetFields();
				}
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<div style={{ marginBottom: 20 }}>
					<FleetTitle>Edit Data Covenant </FleetTitle>
				</div>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={(values) => {
						if (data?.action === 'new') {
							close(values);
						} else {
							close({
								...data?.record,
								...values,
							});
						}
					}}
				>
					<Form.Item
						name={fieldName('id')}
						noStyle
					/>
					{editAllField ? (
						<Form.Item
							name={fieldName('covenant')}
							label="Covenant"
							labelCol={{ span: 6 }}
							wrapperCol={{ span: 18 }}
							rules={[rule.required()]}
						>
							<Input.TextArea rows={3} />
						</Form.Item>
					) : (
						<Form.Item
							label="Covenant"
							labelCol={{ span: 6 }}
							wrapperCol={{ span: 18 }}
						>
							<Input.TextArea
								rows={3}
								disabled
								value={data?.record.covenant}
							/>
						</Form.Item>
					)}
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							{editAllField ? (
								<Form.Item
									name={fieldName('categoryId')}
									label="Kategori"
									rules={[rule.required()]}
									labelCol={{ span: 12 }}
									wrapperCol={{ span: 12 }}
								>
									<Select
										options={categoryOptions?.map((option) => {
											return {
												label: option.value,
												value: option.id,
											};
										})}
									/>
								</Form.Item>
							) : (
								<Form.Item label="Kategori">
									{categoryOptions?.find(
										(option) => option.id === data?.record.categoryId,
									)?.value ?? '-'}
								</Form.Item>
							)}
						</Col>
						<Col span={12}>
							{editAllField ? (
								<Form.Item
									name={fieldName('priorityId')}
									label="Priority"
									rules={[rule.required()]}
									labelCol={{ span: 12 }}
									wrapperCol={{ span: 12 }}
								>
									<Select
										options={priorityOptions?.map((option) => {
											return {
												label: option.value,
												value: option.id,
											};
										})}
									/>
								</Form.Item>
							) : (
								<Form.Item label="Priority">
									{priorityOptions?.find(
										(option) => option.id === data?.record.priorityId,
									)?.value ?? '-'}
								</Form.Item>
							)}
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							{editAllField ? (
								<Form.Item
									name={fieldName('repeatFrequencyId')}
									label="Repeat"
									rules={[rule.required()]}
									labelCol={{ span: 12 }}
									wrapperCol={{ span: 12 }}
								>
									<Select
										options={[
											...(repeatFrequencyOptions || []).map((option) => {
												return {
													label: option.value,
													value: option.id,
												};
											}),
										]}
										onChange={() => {
											form.resetFields([fieldName('repeatFrequencyCustom')]);
										}}
									/>
								</Form.Item>
							) : (
								<Form.Item label="Repeat">
									{repeatFrequencyOptions?.find(
										(option) => option.id === data?.record.repeatFrequencyId,
									)?.value ?? '-'}
								</Form.Item>
							)}
						</Col>
						<Col span={12}>
							{editAllField ? (
								<Form.Item
									name={fieldName('monitoringTypeId')}
									label="Jenis Monitoring"
									rules={[rule.required()]}
									labelCol={{ span: 12 }}
									wrapperCol={{ span: 12 }}
								>
									<Select
										options={monitoringType?.map((option) => {
											return {
												label: option.value,
												value: option.id,
											};
										})}
									/>
								</Form.Item>
							) : (
								<Form.Item label="Jenis Monitoring">
									{monitoringType?.find(
										(option) => option.id === data?.record.monitoringTypeId,
									)?.value ?? '-'}
								</Form.Item>
							)}
						</Col>
					</Row>
					{editAllField && value?.repeatFrequencyId === REPEAT_CUSTOM.id && (
						<div style={{ marginTop: 20 }}>
							<div style={{ marginBottom: 20 }}>
								<FleetTitle>Repeat Custom</FleetTitle>
							</div>
							<Form.Item
								label="Repeat Every"
								required
								labelCol={{ span: 6 }}
								wrapperCol={{ span: 12 }}
							>
								<Row gutter={20}>
									<Col flex="none">
										<Form.Item
											name={fieldName(['repeatFrequencyCustom', 'repeatValue'])}
											rules={[
												rule.required(),
												{
													validator: (_, number) => {
														const { repeatFrequencyId } =
															value.repeatFrequencyCustom ?? {};

														const limit = {
															[REPEAT_FREQUENCY.Day]: 365,
															[REPEAT_FREQUENCY.Week]: 156,
															[REPEAT_FREQUENCY.Month]: 36,
															[REPEAT_FREQUENCY.Year]: 3,
														}[repeatFrequencyId as REPEAT_FREQUENCY];

														if (repeatFrequencyId && number > limit) {
															return Promise.reject(`Maksimal ${limit}`);
														}

														return Promise.resolve();
													},
												},
											]}
											noStyle
										>
											<InputNumber
												min={0}
												style={{ width: 100 }}
											/>
										</Form.Item>
									</Col>
									<Col flex="auto">
										<Form.Item
											name={fieldName([
												'repeatFrequencyCustom',
												'repeatFrequencyId',
											])}
											rules={[rule.required()]}
											noStyle
										>
											<Select
												options={customRepeatFrequencyOptions
													?.sort((a, b) => a.id - b.id)
													?.map((option) => {
														return {
															label: option.value,
															value: option.id,
														};
													})}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
							{customRepeatType && customRepeatType !== 'day' && (
								<Fragment>
									<Divider />
									{customRepeatType === 'week' ? (
										<Row
											gutter={20}
											align="middle"
										>
											<Col span={6}>On</Col>
											<Col flex="none">
												<Form.Item
													name={fieldName([
														'repeatFrequencyCustom',
														'onThePattern',
														'dayOfWeekId',
													])}
													rules={[rule.required()]}
												>
													<Select
														style={{ width: 150 }}
														placeholder="Hari"
														options={dayOfWeekOptions
															?.sort((a, b) => a.id - b.id)
															?.map((option) => {
																return {
																	label: option.value,
																	value: option.id,
																};
															})}
													/>
												</Form.Item>
											</Col>
										</Row>
									) : (
										<Fragment>
											<Row
												gutter={20}
												align="middle"
											>
												<Col span={6}>
													<Radio
														checked={on}
														onChange={(e) => {
															if (e.target.checked) {
																setOn(true);
																form.resetFields([
																	fieldName([
																		'repeatFrequencyCustom',
																		'onThePattern',
																	]),
																]);
																form.validateFields([
																	'repeatFrequencyCustom',
																	'onPattern',
																]);
															}
														}}
													>
														On
													</Radio>
												</Col>
												{customRepeatType === 'year' && (
													<Col flex="none">
														<Form.Item
															name={fieldName([
																'repeatFrequencyCustom',
																'onPattern',
																'monthId',
															])}
															rules={on ? [rule.required()] : []}
														>
															<Select
																disabled={!on}
																style={{ width: 150 }}
																placeholder="Bulan"
																options={monthOptions
																	?.sort((a, b) => a.id - b.id)
																	?.map((option) => {
																		return {
																			label: option.value,
																			value: option.id,
																		};
																	})}
															/>
														</Form.Item>
													</Col>
												)}
												<Col flex="none">
													<Form.Item
														name={fieldName([
															'repeatFrequencyCustom',
															'onPattern',
															'dayOfMonth',
														])}
														rules={on ? [rule.required()] : []}
													>
														<InputNumber
															min={1}
															max={
																value?.repeatFrequencyCustom?.onPattern?.monthId
																	? dayjs()
																			.set(
																				'month',
																				value.repeatFrequencyCustom.onPattern
																					.monthId - 1,
																			)
																			.daysInMonth()
																	: undefined
															}
															disabled={!on}
															style={{ width: 60 }}
														/>
													</Form.Item>
												</Col>
											</Row>
											<Divider />
											<Row
												gutter={20}
												align="middle"
											>
												<Col span={6}>
													<Radio
														checked={!on}
														onChange={(e) => {
															if (e.target.checked) {
																setOn(false);
																form.resetFields([
																	fieldName([
																		'repeatFrequencyCustom',
																		'onPattern',
																	]),
																]);
																form.validateFields([
																	'repeatFrequencyCustom',
																	'onThePattern',
																]);
															}
														}}
													>
														On The
													</Radio>
												</Col>
												<Col flex="none">
													<Form.Item
														name={fieldName([
															'repeatFrequencyCustom',
															'onThePattern',
															'positionId',
														])}
														rules={!on ? [rule.required()] : []}
													>
														<Select
															style={{ width: 150 }}
															disabled={on}
															placeholder="Position"
															options={positionOptions?.map((option) => {
																return {
																	label: option.value,
																	value: option.id,
																};
															})}
														/>
													</Form.Item>
												</Col>
												<Col flex="none">
													<Form.Item
														name={fieldName([
															'repeatFrequencyCustom',
															'onThePattern',
															'dayOfWeekId',
														])}
														rules={!on ? [rule.required()] : []}
													>
														<Select
															style={{ width: 150 }}
															placeholder="Hari"
															disabled={on}
															options={dayOfWeekOptions
																?.sort((a, b) => a.id - b.id)
																?.map((option) => {
																	return {
																		label: option.value,
																		value: option.id,
																	};
																})}
														/>
													</Form.Item>
												</Col>
												{customRepeatType === 'year' && (
													<Fragment>
														<Col flex="none">of</Col>
														<Col flex="none">
															<Form.Item
																name={fieldName([
																	'repeatFrequencyCustom',
																	'onThePattern',
																	'monthId',
																])}
																rules={!on ? [rule.required()] : []}
															>
																<Select
																	style={{ width: 150 }}
																	placeholder="Bulan"
																	disabled={on}
																	options={monthOptions
																		?.sort((a, b) => a.id - b.id)
																		?.map((option) => {
																			return {
																				label: option.value,
																				value: option.id,
																			};
																		})}
																/>
															</Form.Item>
														</Col>
													</Fragment>
												)}
											</Row>
										</Fragment>
									)}
								</Fragment>
							)}
						</div>
					)}
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							{editAllField ? (
								<Form.Item
									name={fieldName('furtherAnalysisId')}
									label="Analisa Lebih Lanjut"
									rules={[rule.required()]}
									labelCol={{ span: 12 }}
									wrapperCol={{ span: 12 }}
								>
									<Select
										options={furtherAnalysisOptions?.map((option) => {
											return {
												label: option.value,
												value: option.id,
											};
										})}
									/>
								</Form.Item>
							) : (
								<Form.Item label="Analisa Lebih Lanjut">
									{furtherAnalysisOptions?.find(
										(option) => option.id === data?.record.furtherAnalysisId,
									)?.value ?? '-'}
								</Form.Item>
							)}
						</Col>
						<Col span={12}>
							{!editAllField && (
								<Form.Item
									name={fieldName('uploadDocumentDate')}
									label="Tanggal Upload Dokumen"
									getValueProps={(value) => ({
										value: value && dayjs(value),
									})}
									normalize={(value) =>
										value && dayjs(value).utc(true).toISOString()
									}
									rules={[rule.required()]}
								>
									<DatePicker />
								</Form.Item>
							)}
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								name={fieldName('covenantDocument')}
								label="Upload Dokumen"
								rules={groupName !== 'CCA' ? [rule.required()] : []}
								labelCol={{ span: 12 }}
								wrapperCol={{ span: 12 }}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: file.id,
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: file.uid,
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
							>
								<Upload.Dragger
									listType="picture"
									accept=".jpg,.jpeg,.png,.pdf"
									maxCount={1}
									beforeUpload={() => false}
									disabled={groupName === 'CCA'}
									style={{
										backgroundColor:
											groupName === 'CCA' ? '#f5f5f5' : 'transparent',
									}}
									onRemove={() => {
										form.setFieldValue(fieldName('covenantDocument'), null);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${customerId}/${prospectId!}/nap/risalah`;

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(fieldName('covenantDocument'), {
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											});
										} catch {
											form.setFieldValue(fieldName('covenantDocument'), null);
										}
									}}
								>
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								</Upload.Dragger>
							</Form.Item>
						</Col>
						<Col span={12}>
							{editAllField ? (
								<Form.Item
									name={fieldName('dueDate')}
									label="Due Date"
									labelCol={{ span: 12 }}
									wrapperCol={{ span: 12 }}
									getValueProps={(value) => ({
										value: value && dayjs(value),
									})}
									normalize={(value) =>
										value && dayjs(value).utc(true).toISOString()
									}
								>
									<DatePicker />
								</Form.Item>
							) : (
								<Form.Item label="Due Date">
									{data?.record.dueDate &&
										dayjs(data.record.dueDate).format('DD/MM/YYYY')}
								</Form.Item>
							)}
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							{editAllField ? (
								<Form.Item
									name={fieldName('statusId')}
									label="Status"
									labelCol={{ span: 12 }}
									wrapperCol={{ span: 12 }}
									rules={groupName !== 'CCA' ? [rule.required()] : []}
								>
									<Select
										disabled={groupName === 'CCA'}
										options={statusOptions?.map((option) => {
											return {
												label: option.value,
												value: option.id,
											};
										})}
									/>
								</Form.Item>
							) : (
								<Form.Item label="Status">
									{statusOptions?.find(
										(option) =>
											(option.id as unknown as string) ===
											data?.record.statusId,
									)?.value ?? '-'}
								</Form.Item>
							)}
						</Col>
					</Row>
					<Divider />
					<Form.Item
						name={fieldName('notes')}
						rules={editAllField ? [rule.required()] : []}
						label="Keterangan"
						labelCol={{ span: 6 }}
						wrapperCol={{ span: 18 }}
					>
						<Input.TextArea rows={4} />
					</Form.Item>
				</Form>
			</div>
		</Modal>
	);
};

export default CovenantModal;
